/*
 * Copyright © 2026 Medaica, Inc
 *
 * All rights reserved.
 *
 * This code is confidential and proprietary information belonging to Medaica, Inc.
 * Unauthorized copying, distribution, or use of this code, in whole or in part,
 * is strictly prohibited, and may constitute a violation of intellectual property rights.
 *
 * If you have received this code in error, please notify the owner immediately
 * at support@medaica.com and delete this file from your system.
 */

// pcg-report core: light-editing / review-flow state rules (no DOM).
// Source: cardiac_report_v0_5_6_dev_2.html lines 3719-3747 (draft-edit predicates,
// approve-and-exit gate), 4411-4444 (note staleness sync + HR-dependent refresh),
// 4287-4312 (resetFeatureToBaseline), 5764-5801 (recording-note transitions).
// Deviations: data globals -> explicit report/features args; Date -> `now` option.

import { HEART_REPORT_APPROVAL_STATEMENT } from "./constants"
import { getColorClass, effectiveFeatureColorClass } from "./status"
import { calculateMarkerPosition } from "./marker"
import { restoreStructuralSources } from "./structural-sources"
import {
  currentHeartRateBpm,
  isCardiacCycleTimingFeature,
  isSysDiaDurationFeature,
  reportFeatures,
  resetFeatureSourcesToBaseline,
  restoreTimingSelection,
} from "./features"
import { setAutoPatientCopy, getAutoProviderNote, providerCopyLooksAutoGenerated } from "./copy"
import { displayReferenceRange } from "./display"
import { buildFeedAgreementLog } from "./governance"
import { approvalBlockReason, includedReportFeatures } from "./inclusion"

// Patient-facing copy is product-controlled, so only a changed result or a
// clinician-authored note counts as a draft edit.
export function featureHasDraftEdits(feature) {
  return !!(feature && (feature.reviewTouched || feature.noteToDoctorEdited))
}

export function reportHasDraftEdits(features, viewOptions) {
  return reportFeatures(features, viewOptions).some(featureHasDraftEdits)
}

// Issuance gate rule: a card in the report whose Clinician Note was written
// against an earlier result blocks approval until the clinician resolves it on
// the card ("Keep my note" or "Use updated suggested note"). Patient copy
// never blocks — it is regenerated, not authored.
export function unresolvedEditFeatures(features) {
  return includedReportFeatures(features).filter((feature) => feature.noteToDoctorStale)
}

// Approves every card in the report. Blocked — with the offending card names,
// or the reason — while a changed Clinician Note is unresolved, while any
// card still needs Save or Exclude, or while no card is included.
export function approveAllFeatures(features, { now = () => new Date().toISOString() } = {}) {
  const unresolved = unresolvedEditFeatures(features)
  if (unresolved.length) {
    return { ok: false, unresolved: unresolved.map((feature) => feature.name), reason: null }
  }
  const reason = approvalBlockReason(features)
  if (reason) return { ok: false, unresolved: [], reason }
  const approvedAt = now()
  const hrBpm = currentHeartRateBpm(features)
  includedReportFeatures(features).forEach((feature) => {
    feature.approved = true
    feature.approvedAt = approvedAt
    feature.feedAgreementLog = buildFeedAgreementLog(feature, { now: () => approvedAt, hrBpm })
  })
  return { ok: true, unresolved: [], reason: null }
}

// Auto-note vs reviewer-owned note reconciliation after a status-affecting edit.
// `copyWasAuto` lets a caller that changed the observed value first (a
// marker drag) pass the auto/edited judgement it made before the change,
// since the generated provider text embeds the observed value.
export function syncNotesAfterStatusChange(features, index, { copyWasAuto = {} } = {}) {
  const feature = features[index]
  if (!feature) return
  const hrBpm = currentHeartRateBpm(features)
  const colorClass = effectiveFeatureColorClass(feature, hrBpm)

  // Patient-facing copy is product-controlled: it always follows the current
  // assessment and is never clinician-authored.
  setAutoPatientCopy(feature, colorClass)
  feature.noteToPatientEdited = false
  feature.noteToPatientStale = false

  if (!feature.noteToDoctorEdited || copyWasAuto.provider || providerCopyLooksAutoGenerated(feature, hrBpm)) {
    feature.noteToDoctor =
      getAutoProviderNote(
        feature.name,
        colorClass,
        feature.observed,
        feature.displayReferenceRange || feature.normalRange,
        hrBpm
      ) || feature.noteToDoctor
    feature.noteToDoctorEdited = false
    feature.noteToDoctorStale = false
  } else {
    feature.noteToDoctorStale = true
  }
}

// Editing Heart Rate re-derives SYS:DIA statuses (their zones are HR-dependent).
export function refreshHeartRateDependentTimingCards(features, changedFeature) {
  if (
    !changedFeature ||
    !String(changedFeature.name || "")
      .toLowerCase()
      .includes("heart rate")
  )
    return
  const hrBpm = currentHeartRateBpm(features)
  ;(features || []).forEach((feature, index) => {
    if (!feature || !(isCardiacCycleTimingFeature(feature) || isSysDiaDurationFeature(feature))) return
    const previousStatus = feature.status
    feature.status = getColorClass(
      feature.name,
      feature.observed,
      feature.normalRange,
      feature.status,
      feature.scaleType,
      hrBpm
    )
    feature.aiStatus = feature.status === "green" ? "normal" : feature.status === "red" ? "abnormal" : "borderline"
    feature.markerPosition = calculateMarkerPosition(feature, hrBpm)
    if (previousStatus !== feature.status) {
      syncNotesAfterStatusChange(features, index)
      feature.approved = false
    } else if (!feature.noteToDoctorEdited) {
      feature.noteToDoctor =
        getAutoProviderNote(
          feature.name,
          feature.status,
          feature.observed,
          displayReferenceRange(feature, hrBpm),
          hrBpm
        ) || feature.noteToDoctor
    }
  })
}

// The "rewind" action: restore the exported (imported-baseline) result.
export function resetFeatureToBaseline(features, index) {
  const feature = features[index]
  if (!feature || !feature.importedBaseline) return false
  const baseline = feature.importedBaseline
  const hrBpm = currentHeartRateBpm(features)
  feature.observed = baseline.observed
  feature.status = baseline.status
  feature.aiStatus = baseline.aiStatus || "normal"
  feature.markerPosition =
    baseline.markerPosition !== undefined ? baseline.markerPosition : calculateMarkerPosition(feature, hrBpm)
  feature.clinicianAssessmentMode = baseline.clinicianAssessmentMode || null
  feature.clinicianRangeBpm = baseline.clinicianRangeBpm ? { ...baseline.clinicianRangeBpm } : null
  feature.sourceRangeBpm = baseline.sourceRangeBpm ? { ...baseline.sourceRangeBpm } : feature.sourceRangeBpm || null
  feature.mixedRange = !!baseline.mixedRange
  restoreTimingSelection(feature, baseline)
  restoreStructuralSources(feature, baseline)
  feature.noteToPatient = baseline.noteToPatient || ""
  feature.patientExplanationText = baseline.patientExplanationText || ""
  feature.patientResultText = baseline.patientResultText || ""
  feature.noteToDoctor = baseline.noteToDoctor || ""
  feature.noteToPatientEdited = false
  feature.noteToDoctorEdited = false
  feature.noteToPatientStale = false
  feature.noteToDoctorStale = false
  resetFeatureSourcesToBaseline(feature, baseline)
  syncNotesAfterStatusChange(features, index)
  feature.reviewTouched = false
  // A card that is not in the report goes back to its neutral start
  if (feature.reportInclusionStatus !== "accepted") feature.manualAssessmentStarted = false
  feature.approved = false
  feature.feedAgreementLog = []
  feature.approvedAt = null
  return true
}

// Withdraw every card approval: the report is back in review (a rebase onto
// a newer snapshot, or the clinician reverting an approved report to a
// draft). Everything else on the cards — values, notes, feed decisions — is
// left as it is.
export function clearFeatureApprovals(features) {
  ;(features || []).forEach((feature) => {
    feature.approved = false
    feature.approvedAt = null
  })
}

// Report-level approval record (Heart Report v0.5.8 `reportApproval`): the
// attestation the clinician confirmed and when. It shares the cards'
// approval timestamp so the document tells one story; the server keeps the
// authoritative copy (editor, timestamp, statement) on the audit row.
export function recordReportApproval(report, { now = () => new Date().toISOString() } = {}) {
  const approvedCard = (report.features || []).find((feature) => feature.approvedAt)
  report.reportApproval = {
    statement: HEART_REPORT_APPROVAL_STATEMENT,
    approvedAt: approvedCard ? approvedCard.approvedAt : now(),
  }
  return report.reportApproval
}

// Withdraws the report-level approval record (revert to draft, reopen).
export function clearReportApproval(report) {
  if (report && "reportApproval" in report) delete report.reportApproval
}

// Reviewer-observation notes. Since Medaica Viewer v0.9.1 (pcg-viewer-mvp
// git 20ac6e4, Medaica Report at cardiac-panel-v4 git eba1cd1) a note's
// structured selections and free text are internal HCP review context
// (`visibility: "internal_review"`); only its separate Report note reaches the
// patient and provider reports. Notes from earlier exports
// (`visibility: "clinical_record"`, no Report note) were report content as a
// whole, and a document saved from them still shows them that way.

// The selection labels and free text of a note, in the dialog's order.
const observationParts = (note) => [
  note.acousticFindingLabel,
  note.dominantPatternLabel ? `Observed pattern: ${note.dominantPatternLabel}` : "",
  ...(Array.isArray(note.otherAcousticObservationLabels) ? note.otherAcousticObservationLabels : []),
  ...(Array.isArray(note.rhythmObservationLabels) ? note.rhythmObservationLabels : []),
  String(note.text || "").trim(),
]

// The internal observation wording (upstream internalObservationText): the
// acoustic rating and confirmed BPM, then the selection labels and the free
// text, " · "-joined.
export function recordingNoteObservationText(note) {
  if (!note) return ""
  return [
    note.acousticQualityLabel,
    note.hcpConfirmedBpm ? `Confirmed BPM ${note.hcpConfirmedBpm}` : "",
    ...observationParts(note),
  ]
    .filter(Boolean)
    .join(" · ")
}

// A note imported before the split: every observation was report content,
// worded as the reviewer's edited `reportText`, else the selection labels and
// free text.
const isPreSplitNote = (note) => note.reportNote === undefined && note.visibility === "clinical_record"

// The Report note: the only wording of a note the report shows and prints
// ("" when the reviewer wrote none).
export function recordingNoteReportNote(note) {
  if (!note) return ""
  if (isPreSplitNote(note)) {
    if (note.reportText !== undefined) return String(note.reportText || "").trim()
    return observationParts(note).filter(Boolean).join(" · ")
  }
  return String(note.reportNote || "").trim()
}

// An edit to the Report note in the report (upstream
// updateRecordingNoteReportNote). The viewer's observations stay untouched.
export function updateRecordingNoteReportNote(note, value, { now = () => new Date().toISOString() } = {}) {
  if (!note) return
  note.reportNote = value
  note.reportEditedAt = now()
}

// Evidence-feed review transition (accept / reject with reason / unreview).
// Any evidence-review change invalidates a previous final-card approval.
export function setSourceStatus(feature, source, status, { rejectReasonCode = null, rejectReasonText = null } = {}) {
  if (!feature || !source) return
  if (status === "rejected") {
    source.reviewStatus = "rejected"
    source.rejectReasonCode = rejectReasonCode
    source.rejectReasonText = rejectReasonText
  } else {
    source.reviewStatus = status
    source.rejectReasonCode = null
    source.rejectReasonText = null
  }
  source._pendingReason = false
  if (feature.approved) {
    feature.approved = false
  }
}
